import React, { useState, useEffect } from 'react';
import { Download, Upload, Play, Trash2, Calendar, Clock, Database, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { backupService, type ConfigurationBackup, type RestoreOperation } from '../services/configurationBackup';
import toast from 'react-hot-toast';
import { PageHeader, LoadingState, EmptyState, StatsGrid, StatCard } from '../components/common';

const BackupRestorePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'backups' | 'restore' | 'schedule'>('backups');
  const [backups, setBackups] = useState<ConfigurationBackup[]>([]);
  const [loading, setLoading] = useState(true);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [restoreOperation, setRestoreOperation] = useState<RestoreOperation | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [, setShowScheduleModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState<ConfigurationBackup | null>(null);
  const [importJson, setImportJson] = useState('');

  useEffect(() => {
    loadBackups();
  }, []);

  const loadBackups = async () => {
    try {
      const backupList = await backupService.listBackups();
      setBackups(backupList);
    } catch (error) {
      toast.error(`Failed to load backups: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async (formData: FormData) => {
    setCreatingBackup(true);
    try {
      const backup = await backupService.createBackup({
        name: formData.get('name') as string,
        description: formData.get('description') as string,
        environment: formData.get('environment') as string,
      });
      
      setBackups(prev => [backup, ...prev]);
      setShowCreateModal(false);
      toast.success('Backup created successfully!');
    } catch (error) {
      toast.error(`Failed to create backup: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setCreatingBackup(false);
    }
  };

  const handleRestoreBackup = async (
    backupId: string,
    options: Parameters<typeof backupService.restoreFromBackup>[1]
  ) => {
    try {
      const operation = await backupService.restoreFromBackup(backupId, options);
      setRestoreOperation(operation);
      setShowRestoreModal(false);
      
      if (operation.status === 'completed') {
        toast.success('Backup restored successfully!');
      } else if (operation.status === 'failed') {
        toast.error(`Restore failed with ${operation.results.failed} errors`);
      }
    } catch (error) {
      toast.error(`Failed to restore backup: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDeleteBackup = async (backupId: string) => {
    if (!window.confirm('Are you sure you want to delete this backup?')) {
      return;
    }

    try {
      await backupService.deleteBackup(backupId);
      setBackups(prev => prev.filter(b => b.id !== backupId));
      toast.success('Backup deleted successfully!');
    } catch (error) {
      toast.error(`Failed to delete backup: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleExportBackup = (backup: ConfigurationBackup) => {
    backupService.exportBackup(backup);
    toast.success('Backup exported successfully!');
  };

  const handleImportBackup = async () => {
    try {
      const backup = await backupService.importBackup(importJson);
      setBackups(prev => [backup, ...prev]);
      setShowImportModal(false);
      setImportJson('');
      toast.success('Backup imported successfully!');
    } catch (error) {
      toast.error(`Failed to import backup: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'running': return 'text-blue-600 bg-blue-100';
      case 'failed': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Backup & Restore"
        description="Manage configuration backups and restore operations"
      />

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('backups')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'backups'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Database className="w-4 h-4" />
              <span>Backups</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('restore')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'restore'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <RefreshCw className="w-4 h-4" />
              <span>Restore Operations</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('schedule')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'schedule'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4" />
              <span>Scheduled Backups</span>
            </div>
          </button>
        </nav>
      </div>

      {/* Backups Tab */}
      {activeTab === 'backups' && (
        <div className="space-y-6">
          {/* Actions */}
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Configuration Backups</h2>
              <p className="text-sm text-gray-600">Create and manage system configuration backups</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowImportModal(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import Backup
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                disabled={creatingBackup}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {creatingBackup ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Database className="w-4 h-4 mr-2" />
                    Create Backup
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Backups List */}
          {loading ? (
            <LoadingState />
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {backups.map((backup) => (
                  <li key={backup.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
                            <Database className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-medium text-gray-900">{backup.name}</h3>
                            <p className="text-sm text-gray-600">{backup.description}</p>
                          </div>
                        </div>
                        
                        <div className="mt-3 flex items-center space-x-6 text-sm text-gray-500">
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-4 h-4" />
                            <span>{new Date(backup.createdAt).toLocaleString()}</span>
                          </div>
                          <span>Size: {formatFileSize(backup.size)}</span>
                          <span>Configurations: {backup.metadata.totalConfigurations}</span>
                          <span>Environment: {backup.environment}</span>
                        </div>

                        <div className="mt-2 flex flex-wrap gap-1">
                          {backup.metadata.configurationTypes.map((type) => (
                            <span
                              key={type}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-blue-600 bg-blue-100"
                            >
                              {type.replace(/([A-Z])/g, ' $1').trim()}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedBackup(backup);
                            setShowRestoreModal(true);
                          }}
                          className="p-2 text-gray-400 hover:text-blue-600"
                          title="Restore Backup"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleExportBackup(backup)}
                          className="p-2 text-gray-400 hover:text-green-600"
                          title="Export Backup"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteBackup(backup.id)}
                          className="p-2 text-gray-400 hover:text-red-600"
                          title="Delete Backup"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Restore Operations Tab */}
      {activeTab === 'restore' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Restore Operations</h2>
            <p className="text-sm text-gray-600">Monitor backup restore operations and their status</p>
          </div>

          {restoreOperation ? (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Restore Operation</h3>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(restoreOperation.status)}`}>
                  {restoreOperation.status}
                </span>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Progress</span>
                  <span className="text-sm font-medium text-gray-900">{restoreOperation.progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${restoreOperation.progress}%` }}
                  ></div>
                </div>

                <StatsGrid>
                  <StatCard
                    label="Total"
                    value={restoreOperation.results.total}
                    icon={Database}
                    iconBgColor="bg-gray-100"
                    iconColor="text-gray-600"
                  />
                  <StatCard
                    label="Successful"
                    value={restoreOperation.results.successful}
                    icon={CheckCircle}
                    iconBgColor="bg-green-100"
                    iconColor="text-green-600"
                    valueColor="text-green-600"
                  />
                  <StatCard
                    label="Failed"
                    value={restoreOperation.results.failed}
                    icon={AlertCircle}
                    iconBgColor="bg-red-100"
                    iconColor="text-red-600"
                    valueColor="text-red-600"
                  />
                  <StatCard
                    label="Skipped"
                    value={restoreOperation.results.skipped}
                    icon={Clock}
                    iconBgColor="bg-yellow-100"
                    iconColor="text-yellow-600"
                    valueColor="text-yellow-600"
                  />
                </StatsGrid>

                {restoreOperation.errors.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Errors</h4>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <ul className="space-y-1">
                        {restoreOperation.errors.map((error, index) => (
                          <li key={index} className="text-sm text-red-700">
                            <span className="font-medium">{error.type}:</span> {error.item} - {error.error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <EmptyState
              title="No restore operations"
              description="Restore operations will appear here when you restore a backup."
              icon={RefreshCw}
            />
          )}
        </div>
      )}

      {/* Scheduled Backups Tab */}
      {activeTab === 'schedule' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Scheduled Backups</h2>
              <p className="text-sm text-gray-600">Automate backup creation with scheduled tasks</p>
            </div>
            <button
              onClick={() => setShowScheduleModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              <Clock className="w-4 h-4 mr-2" />
              Create Schedule
            </button>
          </div>

          <EmptyState
            title="No scheduled backups"
            description="Create automated backup schedules to ensure regular configuration backups."
            icon={Clock}
            action={{
              label: 'Create Schedule',
              onClick: () => setShowScheduleModal(true),
            }}
          />
        </div>
      )}

      {/* Create Backup Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-lg shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create Configuration Backup</h3>
              <form onSubmit={(e) => {
                e.preventDefault();
                handleCreateBackup(new FormData(e.currentTarget));
              }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Backup Name</label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Production Backup"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      name="description"
                      rows={3}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Optional description of this backup"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Environment</label>
                    <select
                      name="environment"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="production">Production</option>
                      <option value="staging">Staging</option>
                      <option value="development">Development</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creatingBackup}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    Create Backup
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Restore Modal */}
      {showRestoreModal && selectedBackup && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-lg shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Restore Configuration</h3>
              <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex">
                  <AlertCircle className="w-5 h-5 text-yellow-400" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-yellow-800">Warning</h4>
                    <p className="text-sm text-yellow-700">
                      This will restore configurations from the backup. Existing configurations may be overwritten.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                handleRestoreBackup(selectedBackup.id, {
                  overwriteExisting: formData.get('overwrite') === 'on',
                  dryRun: formData.get('dryRun') === 'on'
                });
              }}>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="overwrite"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Overwrite existing configurations
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="dryRun"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Dry run (preview only, don't make changes)
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowRestoreModal(false);
                      setSelectedBackup(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Restore Backup
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Import Configuration Backup</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Backup JSON
                  </label>
                  <textarea
                    value={importJson}
                    onChange={(e) => setImportJson(e.target.value)}
                    rows={12}
                    className="w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    placeholder="Paste your backup JSON here..."
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportJson('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImportBackup}
                  disabled={!importJson.trim()}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  Import Backup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackupRestorePage;